import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchE2ETestDashboard, runE2ETests } from "@/api/e2eTest";
import { queryKeys } from "@/shared/libs/queryClient";
import { toast } from "@/shared/hooks/useToast";
import type { RunE2ETestRequest } from "@/types/collection-types";

/**
 * E2E 테스트 대시보드 데이터 조회 훅
 */
export function useE2ETestDashboardQuery() {
  return useQuery({
    queryKey: queryKeys.e2eTests.dashboard(),
    queryFn: fetchE2ETestDashboard,
    refetchInterval: 30000, // 30초마다 자동 갱신
  });
}

/**
 * E2E 테스트 실행 mutation 훅
 */
export function useRunE2ETestsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: RunE2ETestRequest) => runE2ETests(request),
    onSuccess: (data) => {
      // 성공/실패 결과에 따라 다른 메시지 표시
      if (data.success_count > 0 && data.error_count === 0) {
        toast({
          title: "E2E 테스트 실행 완료",
          description: `${data.success_count}개 자산의 E2E 테스트가 성공적으로 요청되었습니다.`,
        });
      } else if (data.success_count > 0 && data.error_count > 0) {
        toast({
          title: "일부 E2E 테스트 실행 실패",
          description: `${data.success_count}개 성공, ${data.error_count}개 실패했습니다.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "E2E 테스트 실행 실패",
          description: "모든 E2E 테스트 요청이 실패했습니다.",
          variant: "destructive",
        });
      }

      // 대시보드 데이터 갱신
      queryClient.invalidateQueries({ queryKey: queryKeys.e2eTests.all });
    },
    onError: (error) => {
      toast({
        title: "E2E 테스트 실행 실패",
        description: error instanceof Error ? error.message : "알 수 없는 오류가 발생했습니다.",
        variant: "destructive",
      });
    },
  });
}
